package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.tedd.db.Note
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

class NoteRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Note>> =
        db.noteQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeById(id: String): Flow<Note?> =
        db.noteQueries.selectById(id).asFlow().mapToOneOrNull(Dispatchers.Default)

    fun observeByParent(parentKind: ParentKind, parentId: String): Flow<List<Note>> =
        db.noteQueries.selectByParent(parentKind.raw, parentId).asFlow().mapToList(Dispatchers.Default)

    fun observePinned(): Flow<List<Note>> =
        db.noteQueries.selectPinned().asFlow().mapToList(Dispatchers.Default)

    fun observeFavorite(): Flow<List<Note>> =
        db.noteQueries.selectFavorite().asFlow().mapToList(Dispatchers.Default)

    /** La corbeille : les notes supprimées depuis moins de [TRASH_DAYS] jours. */
    fun observeTrash(): Flow<List<Note>> =
        db.noteQueries.selectTrash(trashCutoff()).asFlow().mapToList(Dispatchers.Default)

    suspend fun create(
        parentKind: ParentKind,
        body: String,
        parentId: String? = null,
        title: String? = null,
        color: String? = null,
        isPinned: Boolean = false,
        isFavorite: Boolean = false,
        tags: String? = null,
        attachments: String? = null,
        orderIndex: Long = 0,
        id: String = newId("n"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.noteQueries.insert(
            id,
            parentKind.raw,
            parentId,
            title,
            body,
            color,
            if (isPinned) 1 else 0,
            if (isFavorite) 1 else 0,
            tags,
            attachments,
            orderIndex,
            now,
            now,
        )
        id
    }

    suspend fun update(
        id: String,
        parentKind: ParentKind,
        parentId: String?,
        title: String?,
        body: String,
        color: String?,
        isPinned: Boolean,
        isFavorite: Boolean,
        tags: String?,
        orderIndex: Long,
        attachments: String? = null,
    ) = withContext(Dispatchers.Default) {
        db.noteQueries.update(
            parentKind.raw,
            parentId,
            title,
            body,
            color,
            if (isPinned) 1 else 0,
            if (isFavorite) 1 else 0,
            tags,
            attachments,
            orderIndex,
            nowMillis(),
            id,
        )
    }

    /** Le titre et le texte, sans toucher au reste de la note. */
    suspend fun updateText(id: String, title: String?, body: String) = withContext(Dispatchers.Default) {
        db.noteQueries.updateText(title, body, nowMillis(), id)
    }

    suspend fun setParent(id: String, parentKind: ParentKind, parentId: String?) = withContext(Dispatchers.Default) {
        db.noteQueries.setParent(parentKind.raw, parentId, nowMillis(), id)
    }

    /** Les étiquettes, déjà normalisées (voir parseTags) ; null pour aucune. */
    suspend fun setTags(id: String, tags: String?) = withContext(Dispatchers.Default) {
        db.noteQueries.setTags(tags, nowMillis(), id)
    }

    suspend fun setColor(id: String, color: String?) = withContext(Dispatchers.Default) {
        db.noteQueries.setColor(color, nowMillis(), id)
    }

    /**
     * Change les pièces jointes de la note : [transform] reçoit la colonne telle qu'elle
     * est en base, et rend la nouvelle. Lire et écrire dans la même transaction : deux
     * enregistrements rapprochés d'un dessin ne s'écrasent pas l'un l'autre.
     */
    suspend fun updateAttachments(id: String, transform: (String?) -> String?) = withContext(Dispatchers.Default) {
        db.transaction {
            val current = db.noteQueries.selectByIdAnyState(id).executeAsOneOrNull()?.attachments
            val next = transform(current)
            if (next != current) db.noteQueries.setAttachments(next, nowMillis(), id)
        }
    }

    /**
     * Une copie de [note], rangée au même endroit, avec son texte, ses étiquettes et sa
     * couleur. Ni épinglée ni favorite : la copie n'a encore rien mérité.
     */
    suspend fun duplicate(note: Note): String = create(
        parentKind = ParentKind.fromRaw(note.parent_kind) ?: ParentKind.Standalone,
        parentId = note.parent_id,
        title = note.title.orEmpty().ifBlank { "Sans titre" } + " (copie)",
        body = note.body,
        color = note.color,
        tags = note.tags,
        attachments = note.attachments,
        orderIndex = note.order_index,
    )

    suspend fun setPinned(id: String, pinned: Boolean) = withContext(Dispatchers.Default) {
        db.noteQueries.setPinned(if (pinned) 1 else 0, nowMillis(), id)
    }

    suspend fun setFavorite(id: String, favorite: Boolean) = withContext(Dispatchers.Default) {
        db.noteQueries.setFavorite(if (favorite) 1 else 0, nowMillis(), id)
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.noteQueries.softDelete(now, now, id)
    }

    /** Sort une note de la corbeille. Le serveur l'apprendra au prochain envoi. */
    suspend fun restore(id: String) = withContext(Dispatchers.Default) {
        db.noteQueries.restoreFromTrash(nowMillis(), id)
    }

    /**
     * Détruit une note de la corbeille. Si le serveur ne sait pas encore qu'elle est
     * supprimée, la détruire maintenant la ferait revenir vivante au prochain pull : on
     * la retire seulement de la corbeille, et la purge la détruira une fois envoyée.
     */
    suspend fun deleteForever(id: String) = withContext(Dispatchers.Default) {
        db.transaction {
            db.noteQueries.forget(id)
            db.noteQueries.hardDeleteIfSynced(id)
        }
    }

    /** Vide la corbeille, avec les mêmes précautions que [deleteForever]. */
    suspend fun emptyTrash() = withContext(Dispatchers.Default) {
        db.transaction {
            db.noteQueries.selectTrash(trashCutoff()).executeAsList().forEach { db.noteQueries.forget(it.id) }
            db.noteQueries.purgeTrash(trashCutoff())
        }
    }

    /** Détruit ce qui dort dans la corbeille depuis plus de [TRASH_DAYS] jours. */
    suspend fun purgeTrash() = withContext(Dispatchers.Default) {
        db.noteQueries.purgeTrash(trashCutoff())
    }

    private fun trashCutoff(): Long = nowMillis() - TRASH_DAYS * 86_400_000L

    companion object {
        const val TRASH_DAYS = 30L
    }
}
